<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { CircularProgress, DualAxisChart, Loader, Text } from '@Shelly/shelly-design-system';
	import { user_settings } from 'src/stores';
	import type { ChartDataInterface } from 'src/types/Chart';
	import type { GetStatsResponseHistoryData } from 'src/types/requests/charts/get_stats_period';
	import {
		aggregateTariffBuckets,
		buildTariffPressureChart,
		buildTariffSummary,
		getCurrentTariffDetails,
		getPeakCountdownLabel,
		type TariffBucket,
	} from 'src/components/EnergyMonitoring/helpers/energy_tariff_helpers';
	import { fetchEnergyStatistics } from '../helpers/fetch_energy_statistics';
	import { buildHeatmapTodayControls } from '../helpers/heatmap_period_helpers';
	import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
	import type { EnergyMonitoringPeriodId } from '../helpers/period_helpers';
	import { isEnergyMonitoringDemoMode } from '../helpers/energy_data_mode';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';
	import { buildDemoEnergyMonitoringStats } from '../helpers/energy_demo_providers';
	import EmWidgetShell from '../EmWidgetShell.svelte';
	import EmChartLegend from '../EmChartLegend.svelte';
	import EmWidgetState from '../EmWidgetState.svelte';
	import {
		ENERGY_TARIFF_SETTINGS_HREF,
		hasConfiguredTariffValue,
	} from '../helpers/energy_widget_state_helpers';
	import { currentUnitId } from 'src/stores/account/home';

	let todayChartData: ChartDataInterface | null = null;
	let todayTariffBuckets: TariffBucket[] = [];
	let todayHistory: GetStatsResponseHistoryData[] = [];
	let todayLoading = true;
	let todayError = false;
	let loadToken = 0;
	let demoMode = false;
	let clockTick = Date.now();
	let currentTariff: ReturnType<typeof getCurrentTariffDetails> = null;
	let peakCountdown: string | null = null;
	export let staticPreview = false;
	export let controls: ChartControlsDataInterface | undefined;
	export let periodId: EnergyMonitoringPeriodId = '24_hours';
	const demoStats = buildDemoEnergyMonitoringStats();

	$: tariffLegend = [
		{
			id: 'consumption',
			label: $format('energy_monitoring_tariff_usage_legend', { default: 'Usage (kWh)' }),
			color: 'var(--color-chart-consumed-energy)',
		},
		{
			id: 'cost',
			label: $format('energy_monitoring_tariff_cost_legend', {
				default: 'Cost ({currency})',
				values: { currency },
			}),
			color: 'var(--color-chart-money-value)',
		},
	];

	$: {
		$energyMonitoringDataMode;
		demoMode = staticPreview || isEnergyMonitoringDemoMode();
	}
	$: loading = demoMode ? false : todayLoading;
	$: tariffBuckets = demoMode ? demoStats.tariffBuckets : todayTariffBuckets;
	$: chartData = demoMode ? demoStats.chartData : todayChartData;
	$: currency = demoMode ? 'EUR' : ($user_settings?.currency ?? '');
	$: tariffSummary = buildTariffSummary(tariffBuckets, currency);
	$: equalRateSchedule = tariffSummary.hasTariffs && !tariffSummary.hasDistinctRates;
	$: tariffChart = buildTariffPressureChart(chartData, demoMode ? [] : todayHistory, tariffBuckets);
	$: tariffBarKeys =
		tariffChart?.series.filter((series) => series.name !== 'cost').map((series) => series.name) ??
		[];
	$: tariffChartColors = [
		...tariffBarKeys.map(
			(key) =>
				`var(${tariffSummary.rows.find((row) => `tariff-${row.id}` === key)?.color ?? '--color-chart-consumed-energy'})`,
		),
		'var(--color-chart-money-value)',
	];
	$: hasConfiguredTariffs = hasConfiguredTariffValue($user_settings);
	$: {
		$user_settings;
		clockTick;
		currentTariff = demoMode
			? { name: 'Off-peak', priceLabel: '€0.12/kWh', tone: 'offPeak' }
			: getCurrentTariffDetails(currency);
		peakCountdown = demoMode ? null : getPeakCountdownLabel();
	}
	$: peakExposureLabel = equalRateSchedule
		? $format('energy_monitoring_equal_rates', { default: 'Equal rates' })
		: tariffSummary.peakPercent <= 35
			? $format('energy_monitoring_tariff_exposure_good', { default: 'Good' })
			: tariffSummary.peakPercent <= 65
				? $format('energy_monitoring_tariff_exposure_moderate', { default: 'Moderate' })
				: $format('option_high', { default: 'High' });
	$: peakExposureClass = equalRateSchedule
		? 'em-tariff-exposure-equal'
		: tariffSummary.peakPercent <= 35
			? 'em-tariff-exposure-good'
			: tariffSummary.peakPercent <= 65
				? 'em-tariff-exposure-moderate'
				: 'em-tariff-exposure-high';

	const loadTodayTariffData = async () => {
		const token = ++loadToken;
		if (staticPreview || isEnergyMonitoringDemoMode()) {
			todayError = false;
			todayLoading = false;
			return;
		}
		todayLoading = true;
		todayError = false;
		try {
			const result = await fetchEnergyStatistics(
				controls ?? buildHeatmapTodayControls(),
				periodId,
				undefined,
			);
			if (token !== loadToken) return;
			todayChartData = result?.chartData ?? null;
			todayHistory = result?.history ?? [];
			todayTariffBuckets = aggregateTariffBuckets(todayHistory);
		} catch {
			if (token !== loadToken) return;
			todayChartData = null;
			todayHistory = [];
			todayTariffBuckets = [];
			todayError = true;
		} finally {
			if (token === loadToken) todayLoading = false;
		}
	};

	$: {
		$currentUnitId;
		$energyMonitoringDataMode;
		staticPreview;
		controls;
		periodId;
		void loadTodayTariffData();
	}

	onMount(() => {
		let minuteTicks = 0;
		const timer = window.setInterval(() => {
			clockTick = Date.now();
			minuteTicks += 1;
			if (minuteTicks % 5 === 0) void loadTodayTariffData();
		}, 60_000);
		return () => window.clearInterval(timer);
	});

	onDestroy(() => {
		loadToken += 1;
	});
</script>

<EmWidgetShell
	widgetClass="energy-widget-tariff"
	title={$format('energy_monitoring_tariff_title', { default: 'Tariff pressure' })}
	infoText={$format('energy_monitoring_tariff_info', {
		default: equalRateSchedule
			? 'Usage and reported cost for an equal-rate tariff schedule in the selected period.'
			: 'Usage, reported cost, and exposure to your highest-priced tariff band for the selected period.',
	})}
>
	{#if loading}
		<div class="em-tariff-state">
			<Loader size="m" />
		</div>
	{:else if todayError}
		<EmWidgetState
			kind="error"
			minHeight="180px"
			message={$format('energy_monitoring_load_error', {
				default: 'Tariff data could not be loaded.',
			})}
			onRefresh={loadTodayTariffData}
		/>
	{:else if tariffSummary.hasTariffs}
		<div class="em-tariff-body">
			{#if !demoMode && !hasConfiguredTariffs}
				<EmWidgetState
					kind="setup"
					compact
					message={$format('energy_monitoring_tariff_partial_setup', {
						default: 'Consumption is available, but cost and tariff insights need a tariff.',
					})}
					actionHref={ENERGY_TARIFF_SETTINGS_HREF}
					actionLabel={$format('energy_monitoring_tariff_configure', {
						default: 'Set up tariff',
					})}
				/>
			{/if}
			<div class="em-tariff-kpis">
				<div class="em-tariff-kpi em-tariff-current">
					<div
						class="em-tariff-current-name"
						class:em-tariff-current-name-peak={currentTariff?.tone === 'peak'}
					>
						<span
							class="em-tariff-status-dot"
							class:em-tariff-status-dot-peak={currentTariff?.tone === 'peak'}
						></span>
						<Text styleType="body-small" class="text-600">
							{equalRateSchedule
								? $format('energy_monitoring_tariff_now', { default: 'Tariff now' })
								: currentTariff?.tone === 'peak'
									? $format('energy_monitoring_tariff_peak_now', {
											default: 'Peak now',
										})
									: currentTariff?.tone === 'offPeak'
										? $format('energy_monitoring_tariff_off_peak_now', {
												default: 'Off-peak now',
											})
										: (currentTariff?.name ??
											$format('energy_monitoring_tariff_current', {
												default: 'Current tariff',
											}))}
						</Text>
					</div>
					<div class="em-tariff-price">
						<Text styleType="h1" class="text-600">
							{currentTariff?.priceLabel ?? '—'}
						</Text>
					</div>
					{#if peakCountdown && currentTariff?.tone !== 'peak'}
						<Text styleType="miniature" class="text-color-muted">
							{peakCountdown}
						</Text>
					{/if}
				</div>

				<div class="em-tariff-kpi">
					<Text styleType="h1" class="text-600 em-tariff-cost">
						{tariffSummary.totalCost ?? '—'}
					</Text>
					<Text styleType="body-small" class="text-color-muted">
						{$format('chart_legend_cost', { default: 'Cost' })}
					</Text>
				</div>

				<div class="em-tariff-kpi">
					<Text styleType="h1" class="text-600 em-tariff-used">
						{tariffSummary.totalConsumptionLabel}
					</Text>
					<Text styleType="body-small" class="text-color-muted">
						{$format('energy_monitoring_tariff_used', { default: 'Used' })}
					</Text>
				</div>

				<div class="em-tariff-kpi em-tariff-exposure">
					{#if !equalRateSchedule}
						<CircularProgress
							size="m"
							progress={tariffSummary.peakPercent}
							backgroundColor="--color-2"
						/>
					{/if}
					<div>
						<Text styleType="body-small" class="text-color-muted">
							{equalRateSchedule
								? $format('energy_monitoring_tariff_schedule', {
										default: 'Tariff schedule',
									})
								: $format('energy_monitoring_tariff_peak_exposure', {
										default: 'Peak exposure',
									})}
						</Text>
						<Text styleType="body-small" class="text-600 {peakExposureClass}">
							{peakExposureLabel}
						</Text>
					</div>
				</div>
			</div>

			{#if tariffChart}
				{#key tariffChart.series?.[0]?.data?.length}
					<div class="em-tariff-chart-block">
						<div class="em-tariff-chart-stage">
							<DualAxisChart
								chartData={tariffChart}
								leftKeys={tariffBarKeys}
								rightKeys={['cost']}
								leftMark="bar"
								rightMark="line"
								rightUnit={currency}
								height={280}
								colorScheme={tariffChartColors}
							/>
						</div>
						<EmChartLegend items={tariffLegend} />
					</div>
				{/key}
			{/if}

			<div class="em-tariff-share">
				<Text styleType="body-small" class="text-600">
					{$format('energy_monitoring_tariff_share', { default: 'Tariff share' })}
				</Text>
				<div class="em-tariff-stack" role="img" aria-label="Tariff share">
					{#each tariffSummary.rows as row (row.id)}
						{#if row.percent > 0}
							<span
								class="em-tariff-segment"
								style={`width: ${row.percent}%; background: var(${row.color});`}
							>
								{#if row.percent >= 12}
									<Text styleType="miniature" class="text-white">{row.percent}%</Text>
								{/if}
							</span>
						{/if}
					{/each}
				</div>
				<div class="em-tariff-rows">
					{#each tariffSummary.rows as row (row.id)}
						<div class="em-tariff-row-head">
							<span class="em-tariff-dot" style={`background: var(${row.color});`}></span>
							<Text styleType="body-small" class="text-600 em-tariff-name">{row.name}</Text
							>
							<Text styleType="body-small" class="text-color-muted em-tariff-value">
								{row.consumptionLabel}
								{#if row.costLabel}
									· {row.costLabel}
								{/if}
							</Text>
							<Text
								styleType="body-small"
								class="text-600 em-tariff-percent"
								style={`color: var(${row.color})`}
							>
								{row.percent}%
							</Text>
						</div>
					{/each}
				</div>
			</div>
		</div>
	{:else if !hasConfiguredTariffs}
		<EmWidgetState
			kind="setup"
			minHeight="180px"
			message={$format('energy_monitoring_tariff_empty', {
				default: 'Set up an electricity tariff to see costs and tariff insights.',
			})}
			actionHref={ENERGY_TARIFF_SETTINGS_HREF}
			actionLabel={$format('energy_monitoring_tariff_configure', {
				default: 'Set up tariff',
			})}
		/>
	{:else}
		<EmWidgetState
			minHeight="180px"
			message={$format('energy_monitoring_state_no_activity_message', {
				default: 'No activity to display.',
			})}
		/>
	{/if}
</EmWidgetShell>

<style lang="scss">
	.em-tariff-state {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 180px;
	}

	.em-tariff-body {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-m);
		container-type: inline-size;
		container-name: tariff-pressure;
		min-width: 0;
	}

	.em-tariff-kpis {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		min-width: 0;
	}

	.em-tariff-kpi {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-xs);
		min-width: 0;
		padding: var(--spacing-s) var(--spacing-m);
		text-align: center;
	}

	.em-tariff-kpi + .em-tariff-kpi {
		border-left: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-tariff-current {
		align-items: flex-start;
		text-align: left;
	}

	.em-tariff-current-name,
	.em-tariff-price,
	.em-tariff-exposure {
		display: flex;
		align-items: center;
	}

	.em-tariff-current-name {
		gap: var(--spacing-s);
		color: var(--color-success);
	}

	.em-tariff-status-dot {
		width: var(--spacing-s);
		height: var(--spacing-s);
		border-radius: var(--radius-pill);
		background: var(--color-success);
		box-shadow: var(--shadow-glow-soft);
	}

	.em-tariff-status-dot-peak {
		background: var(--color-danger);
	}

	.em-tariff-current-name-peak {
		color: var(--color-danger);
	}

	.em-tariff-price {
		align-items: baseline;
	}

	.em-tariff-kpis :global(.em-tariff-cost) {
		color: var(--color-chart-money-value);
	}

	.em-tariff-kpis :global(.em-tariff-used) {
		color: var(--color-danger);
	}

	.em-tariff-exposure {
		flex-direction: row;
		gap: var(--spacing-s);
		text-align: left;
	}

	.em-tariff-exposure :global(.em-tariff-exposure-good) {
		color: var(--color-success);
	}

	.em-tariff-exposure :global(.em-tariff-exposure-moderate) {
		color: var(--color-warning);
	}

	.em-tariff-exposure :global(.em-tariff-exposure-high) {
		color: var(--color-danger);
	}

	.em-tariff-exposure :global(.em-tariff-exposure-equal) {
		color: var(--color-text);
	}

	.em-tariff-chart-block {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		padding: var(--spacing-s) var(--spacing-m);
	}

	.em-tariff-chart-stage {
		min-width: 0;
	}

	.em-tariff-chart-stage :global(.layer-chart-theme),
	.em-tariff-chart-stage :global(svg) {
		overflow: visible;
	}

	.em-tariff-share {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
		padding: var(--spacing-s);
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 16px;
		background: color-mix(in srgb, var(--color-1) 34%, transparent);
	}

	.em-tariff-stack {
		display: flex;
		width: 100%;
		height: var(--spacing-l);
		border-radius: var(--radius-small);
		overflow: hidden;
		background: var(--color-2);
		gap: var(--spacing-xxs);
	}

	.em-tariff-segment {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		min-width: var(--spacing-xxs);
		transition: width 200ms;
	}

	.em-tariff-rows {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.em-tariff-row-head {
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);
	}

	.em-tariff-dot {
		width: var(--spacing-m);
		height: var(--spacing-m);
		border-radius: var(--radius-pill);
		flex: 0 0 auto;
	}

	.em-tariff-row-head :global(.em-tariff-name) {
		min-width: 0;
	}

	.em-tariff-row-head :global(.em-tariff-value) {
		margin-left: auto;
		white-space: nowrap;
	}

	.em-tariff-row-head :global(.em-tariff-percent) {
		flex: 0 0 auto;
		min-width: calc(var(--spacing-xl) + var(--spacing-s));
		text-align: right;
	}

	@container tariff-pressure (max-width: 560px) {
		.em-tariff-kpis {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.em-tariff-kpi:nth-child(odd) {
			border-left: 0;
		}

		.em-tariff-kpi:nth-child(n + 3) {
			border-top: 1px solid rgba(255, 255, 255, 0.12);
		}

		.em-tariff-chart-block {
			padding: var(--spacing-xs) var(--spacing-s);
		}
	}

	@container tariff-pressure (max-width: 420px) {
		.em-tariff-row-head {
			flex-wrap: wrap;
		}

		.em-tariff-row-head :global(.em-tariff-value) {
			order: 4;
			width: 100%;
			margin-left: calc(var(--spacing-m) + var(--spacing-xs));
		}
	}
</style>
